/**
 * QuoteGeneratorPage — build a customer quote from the live OptiSigns catalog and print
 * it to PDF.
 *
 * A full page rather than a dialog: the quote is a Letter-sized document, and printing
 * out of a scrolling modal fights the print pipeline. Reached from the Quote Generator
 * card in the OptiHub Billing Console.
 *
 * All pricing is the deterministic engine in `../pricing` — a 1:1 port of the OptiClaw
 * quote-gen Python engine, so a rep's quote and an autonomous one agree to the cent.
 * Nothing is persisted: the quote lives in this page's state until printed.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ArrowLeft, Plus, Printer, RotateCcw } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { buildQuote } from '../pricing/build-quote';
import { formatMoney } from '../pricing/cents';
import { CURRENCIES } from '../pricing/types';
import type {
  QuoteCustomer,
  QuoteRequest,
  QuoteRequestLine,
} from '../pricing/types';
import { parseQuotePrefill } from '../prefill';
import { useQuoteCatalog } from '../hooks/useQuoteCatalog';
import { CountryCombobox } from '../components/CountryCombobox';
import { currencyForCountry } from '../currency-for-country';
import { countryLabel, normalizeCountry } from '../countries';
import { QuoteLineRows } from '../components/QuoteLineRows';
import { QuotePrintView } from '../components/QuotePrintView';
import { QuoteTermsEditor } from '../components/QuoteTermsEditor';
import { defaultTerms, newTerm } from '../terms';
import type { QuoteTerm } from '../terms';

let rowSeq = 0;
function newLine(): QuoteRequestLine {
  rowSeq += 1;
  return { id: `row-${rowSeq}`, kind: 'subscription', quantity: 1, term: 'yearly' };
}

/**
 * Quote reference. OptiClaw derives its `OS-Q-…` suffix from the HubSpot ticket id; there
 * is no ticket here, so it comes from the clock. Quotes aren't persisted, so this is a
 * reference for the conversation, not a key — collisions cost nothing.
 */
function newQuoteNumber(): string {
  return `OS-Q-${Date.now()}`;
}

export function QuoteGeneratorPage() {
  const { catalog, loading, error } = useQuoteCatalog();
  const [searchParams] = useSearchParams();

  /**
   * A deep link seeds the form ONCE, through the useState initialisers — not an effect.
   * Re-deriving state from the URL on every render would revert the operator's edits the
   * moment anything re-rendered, and syncing it in a useEffect is the pattern the repo's
   * data-fetching rule forbids. `useState(() => …)` runs on mount only, which is exactly
   * the semantics we want: the link proposes, the operator owns it from then on.
   *
   * The link can name products and quantities. It cannot set a price — see prefill.ts.
   */
  const [prefill] = useState(() => parseQuotePrefill(searchParams));

  const [currency, setCurrency] = useState<string>(
    prefill.currency ?? currencyForCountry(prefill.customer.country) ?? 'usd',
  );
  /**
   * Who fixed the currency, if anyone — the country stops driving it once someone did.
   * Without this a quote deliberately billed in USD to a German customer would snap back to
   * EUR the moment anyone touched the country field.
   *
   * WHO matters, not just whether: a link naming `currency=` also pins (it was stated, so
   * it wins), but telling the rep "set by hand" when a link set it is a false statement
   * about their own form. They then have no idea why the country isn't taking effect.
   */
  const [pinnedBy, setPinnedBy] = useState<'link' | 'hand' | null>(
    prefill.currency !== undefined ? 'link' : null,
  );
  /** Set when the country last changed the currency, so the field can say why. */
  const [currencyFromCountry, setCurrencyFromCountry] = useState<string | null>(null);
  const [customer, setCustomer] = useState<QuoteCustomer>({ ...prefill.customer });
  const [lines, setLines] = useState<QuoteRequestLine[]>(
    prefill.lines.length ? prefill.lines : [newLine()],
  );
  /**
   * T&C bullets for this quote. Seeded from the standard six; the operator can edit, drop
   * or add lines and none of it leaves the page.
   */
  const [terms, setTerms] = useState<QuoteTerm[]>(defaultTerms);
  const [quoteNumber, setQuoteNumber] = useState(newQuoteNumber);
  const [noticesDismissed, setNoticesDismissed] = useState(false);

  const setCustomerField = (k: keyof QuoteCustomer) => (v: string) =>
    setCustomer((s) => ({ ...s, [k]: v }));

  const request = useMemo<QuoteRequest>(
    () => ({ currency, customer, lines }),
    [currency, customer, lines],
  );

  const quote = useMemo(() => buildQuote(request, catalog), [request, catalog]);

  /** What the country would pick, whether or not the currency is currently following it. */
  const countryCurrency = currencyForCountry(customer.country);
  /**
   * The country's display name for that hint. Safe to normalise unconditionally here:
   * `countryCurrency` is non-null only for a country that resolved, and the hint is the
   * only reader.
   */
  const countryName = countryCurrency
    ? countryLabel(normalizeCountry(customer.country) as string)
    : '';

  // Printing a quote with a blank price is the one thing we hard-block; every other flag
  // is advisory, because a human is reading them. A quote with no priced lines at all is
  // likewise nothing to print.
  const pricedCount = quote.lines.filter((l) => l.amountCents !== null).length;
  const hasUnpriced = quote.lines.some((l) => l.amountCents === null);
  const canPrint = pricedCount > 0 && !hasUnpriced;

  const updateLine = (id: string, patch: Partial<QuoteRequestLine>) =>
    setLines((s) => s.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeLine = (id: string) => setLines((s) => s.filter((l) => l.id !== id));

  const reset = () => {
    // Reset clears the link's seed too — after this the form is a blank quote, not the
    // one the link proposed.
    setNoticesDismissed(true);
    setCurrency('usd');
    setPinnedBy(null);
    setCurrencyFromCountry(null);
    setCustomer({});
    setLines([newLine()]);
    setTerms(defaultTerms());
    setQuoteNumber(newQuoteNumber());
  };

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="quote-generator-page">
      <div className="flex flex-col gap-1 print:hidden">
        <Link
          to="/staff/optihub"
          className="flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {'Back to OptiHub'}
        </Link>
        <h1 className="text-2xl font-bold text-foreground">{'Quote Generator'}</h1>
        <p className="text-sm text-muted-foreground">
          {
            'Build a customer quote from live OptiSigns pricing, then print it to PDF. Annual terms only — nothing is saved.'
          }
        </p>
      </div>

      {error ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive print:hidden"
          data-testid="quote-catalog-error"
        >
          {`Could not load the pricing catalog: ${error.message}`}
        </p>
      ) : null}

      {/* A rep who followed a link needs to know the form was pre-filled — otherwise the
          values look like someone else's quote left on screen — and, more importantly,
          what the link asked for that we could NOT honour. Silently dropping a line
          would mean sending a quote missing something the customer asked for. */}
      {prefill.applied && !noticesDismissed ? (
        <section
          className="flex flex-col gap-2 rounded-lg border border-primary/40 bg-primary/5 p-4 print:hidden"
          data-testid="quote-prefill-banner"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-foreground">
                {prefill.lines.length
                  ? `Pre-filled from a link — ${prefill.lines.length} line${prefill.lines.length === 1 ? '' : 's'} proposed. Check it, then print.`
                  : 'Pre-filled from a link.'}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {'Prices always come from the live catalog — a link can name products and quantities, never prices.'}
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={() => setNoticesDismissed(true)}
              className="cursor-pointer"
              data-testid="quote-prefill-dismiss"
            >
              {'Dismiss'}
            </Button>
          </div>
          {prefill.notices.length ? (
            <ul
              className="list-disc pl-5 text-[11px] text-warning"
              data-testid="quote-prefill-notices"
            >
              {prefill.notices.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <div className="flex flex-col gap-4 print:hidden">
        {/* ── Customer ── */}
        <section
          className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
          data-testid="quote-customer-card"
        >
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-semibold text-foreground">{'Customer'}</h2>
            <p className="text-xs text-muted-foreground">
              {
                'Company is what the quote is addressed to (it falls back to name, then email). Country sets the shipping region — blank counts as US.'
              }
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <LabeledInput
              id="quote-company"
              label="Company"
              value={customer.company ?? ''}
              onChange={setCustomerField('company')}
              placeholder="OptiSigns"
              testId="quote-company"
            />
            <LabeledInput
              id="quote-name"
              label="Contact name"
              value={customer.name ?? ''}
              onChange={setCustomerField('name')}
              placeholder="John Doe"
              testId="quote-name"
            />
            <LabeledInput
              id="quote-email"
              label="Email"
              value={customer.email ?? ''}
              onChange={setCustomerField('email')}
              placeholder="johndoe@example.com"
              testId="quote-email"
            />
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{'Country'}</Label>
              <CountryCombobox
                value={customer.country}
                onChange={(v) => {
                  setCustomer((c) => ({ ...c, country: v }));
                  if (pinnedBy) return;
                  const inferred = currencyForCountry(v);
                  // null = nothing to go on (cleared, or a spelling we can't place) — leave
                  // the currency where it is rather than resetting it.
                  if (inferred) {
                    setCurrency(inferred);
                    setCurrencyFromCountry(inferred);
                  }
                }}
                testId="quote-country"
              />
              <p className="text-[11px] text-muted-foreground">
                {'Sets the shipping rate (United States → domestic, anywhere else → international) and picks the currency.'}
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{'Currency'}</Label>
              <Select
                value={currency}
                onValueChange={(v) => {
                  setCurrency(v);
                  setPinnedBy('hand');
                  setCurrencyFromCountry(null);
                }}
              >
                <SelectTrigger className="cursor-pointer" data-testid="quote-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c} className="cursor-pointer">
                      {c.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground" data-testid="quote-currency-note">
                {pinnedBy === 'link'
                  ? 'Set by the link — the country will not change it.'
                  : pinnedBy === 'hand'
                    ? 'Set by hand — the country will not change it.'
                    : currencyFromCountry
                      ? 'Followed the country. Change it and it stays changed.'
                      : 'Follows the country unless you change it.'}
              </p>
              {/* A pinned currency that disagrees with the country is the one case where
                  "explicit wins" looks like a bug: the rep changes the country, nothing
                  happens, and the reason is invisible. Say so, and offer the switch. */}
              {countryCurrency && countryCurrency !== currency ? (
                <button
                  type="button"
                  className="w-fit cursor-pointer text-[11px] font-medium text-primary underline-offset-2 hover:underline"
                  data-testid="quote-currency-suggest"
                  onClick={() => {
                    setCurrency(countryCurrency);
                    setPinnedBy(null);
                    setCurrencyFromCountry(countryCurrency);
                  }}
                >
                  {`${countryName} is billed in ${countryCurrency.toUpperCase()} — switch`}
                </button>
              ) : null}
            </div>
          </div>
        </section>

        {/* ── Line items ── */}
        <section
          className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
          data-testid="quote-lines-card"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold text-foreground">{'Line items'}</h2>
              <p className="text-xs text-muted-foreground">
                {
                  'Items come from the live catalog. Leave unit price blank to use catalog pricing; type one to override for a negotiated or Enterprise deal.'
                }
              </p>
            </div>
            {loading && !catalog.products.length ? (
              <span className="text-xs text-muted-foreground">{'Loading catalog…'}</span>
            ) : null}
          </div>

          <QuoteLineRows
            lines={lines}
            catalog={catalog}
            currency={currency}
            priced={quote.lines}
            onChange={updateLine}
            onRemove={removeLine}
          />

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setLines((s) => [...s, newLine()])}
              className="cursor-pointer"
              data-testid="quote-add-line"
            >
              <Plus className="mr-1 h-4 w-4" />
              {'Add line'}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              {
                'Standard shipping is added automatically to any quote with hardware, unless you add a shipping line yourself.'
              }
            </p>
          </div>
        </section>

        {/* ── Terms & conditions ── */}
        <QuoteTermsEditor
          terms={terms}
          onChange={(id, text) =>
            setTerms((s) => s.map((t) => (t.id === id ? { ...t, text } : t)))
          }
          onAdd={() => setTerms((s) => [...s, newTerm()])}
          onRemove={(id) => setTerms((s) => s.filter((t) => t.id !== id))}
          onRestoreDefaults={() => setTerms(defaultTerms())}
        />

        {/* ── Warnings ── */}
        {quote.hitl.length ? (
          <section
            className="flex flex-col gap-1.5 rounded-lg border border-warning/40 bg-warning/10 p-4"
            data-testid="quote-warnings"
          >
            <p className="text-xs font-medium text-warning">{'Check before sending'}</p>
            <ul className="list-disc pl-5 text-xs text-warning">
              {quote.hitl.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* ── Totals + actions ── */}
        <section
          className="flex flex-wrap items-end justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm"
          data-testid="quote-totals"
        >
          <dl className="flex flex-col gap-1 text-sm">
            <Row
              label="Subtotal"
              value={formatMoney(quote.subtotalCents, currency)}
              testId="quote-subtotal"
            />
            {quote.discountCents ? (
              <Row
                label={quote.discountNote ?? 'Discount'}
                value={`-${formatMoney(quote.discountCents, currency)}`}
                testId="quote-discount"
              />
            ) : null}
            <Row
              label="Total"
              value={formatMoney(quote.totalCents, currency)}
              emphasis
              testId="quote-total"
            />
          </dl>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={reset}
              className="cursor-pointer"
              data-testid="quote-reset"
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              {'Reset'}
            </Button>
            <Button
              onClick={() => window.print()}
              disabled={!canPrint}
              className="cursor-pointer"
              data-testid="quote-print"
            >
              <Printer className="mr-1 h-4 w-4" />
              {'Print / Save as PDF'}
            </Button>
          </div>
        </section>

        {hasUnpriced ? (
          <p className="text-xs text-destructive" data-testid="quote-print-blocked">
            {
              'Every line needs a price before the quote can be printed — an unpriced line would show a blank amount to the customer.'
            }
          </p>
        ) : null}
      </div>

      {/* The customer-facing document. On screen it's a live preview; @media print keeps
          only this subtree. `quote-print-root` is the hook that print CSS re-shows. */}
      <div className="quote-print-root overflow-x-auto rounded-lg border bg-white print:border-0">
        <QuotePrintView quote={quote} quoteNumber={quoteNumber} terms={terms} />
      </div>
    </div>
  );
}

/**
 * A totals line. `testId` lands on the VALUE, not the row: a test asserting a subtotal
 * wants to compare one formatted figure, and matching against a row that also carries its
 * label passes on substrings of the label.
 */
function Row({
  label,
  value,
  emphasis,
  testId,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  testId?: string;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-xs text-muted-foreground first-letter:uppercase">{label}</dt>
      <dd
        className={emphasis ? 'text-lg font-bold text-foreground' : 'text-sm text-foreground'}
        data-testid={testId}
      >
        {value}
      </dd>
    </div>
  );
}

function LabeledInput(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  helper?: string;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id} className="text-xs">
        {props.label}
      </Label>
      <Input
        id={props.id}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        data-testid={props.testId}
      />
      {props.helper ? <p className="text-[11px] text-muted-foreground">{props.helper}</p> : null}
    </div>
  );
}
